import React, { useState } from 'react';
import { Badge, Button, Form, Input, Modal, Table, message } from 'antd';
import { EditOutlined, PlusOutlined, TranslationOutlined } from '@ant-design/icons';
import { useTranslation } from '../../shared/i18n';
import I18nFieldTabs, { parseI18n, cleanI18n, mergeI18n } from './I18nFieldTabs';

const errText = (e) => e?.response?.data?.error || e?.response?.data?.msg || e?.error || e?.message;

/**
 * Button + list dialog for a small catalog table whose customer-visible text is name / description (+ i18n).
 *   load(): Promise<rows [{id, name, description, i18n}]>
 *   save(row | null, {name, description, i18n}): Promise (throws / rejects with {error} on failure); row null = create
 */
export default function CatalogTextsButton({ title, load, save, canCreate = false, descMax, size }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null); // row, {} = new, null = closed
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    const refresh = async () => {
        setLoading(true);
        try {
            setRows((await load()) || []);
        } catch (e) {
            message.error(errText(e) || t('Failed to load'));
        } finally {
            setLoading(false);
        }
    };
    const show = () => { setOpen(true); refresh(); };
    const edit = (r) => setEditing(r);
    const submit = async () => {
        const v = await form.validateFields();
        setSaving(true);
        try {
            const i18n = cleanI18n(mergeI18n(editing.i18n, v.i18n));
            await save(editing.id ? editing : null, { name: v.name, description: v.description || '', i18n });
            message.success(t('Saved'));
            setEditing(null);
            refresh();
        } catch (e) {
            message.error(errText(e) || t('Failed to save'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Button icon={<TranslationOutlined />} size={size} onClick={show}>{title}</Button>
            <Modal title={title} open={open} onCancel={() => setOpen(false)} footer={null} width={640}>
                {canCreate && (
                    <Button size="small" icon={<PlusOutlined />} style={{ marginBottom: 8 }} onClick={() => edit({})}>{t('Add')}</Button>
                )}
                <Table size="small" rowKey="id" loading={loading} pagination={false} dataSource={rows} columns={[
                    { title: 'ID', dataIndex: 'id', width: 60 },
                    { title: t('Name'), dataIndex: 'name', render: (v, r) => (Object.keys(parseI18n(r.i18n)).length ? <Badge dot offset={[6, 0]}>{v}</Badge> : v) },
                    { title: t('Description'), dataIndex: 'description', ellipsis: true },
                    { title: '', key: 'op', width: 60, render: (_, r) => <Button type="link" size="small" icon={<EditOutlined />} onClick={() => edit(r)} /> },
                ]} />
            </Modal>
            <Modal title={editing?.id ? editing.name : t('Add')} open={!!editing} onCancel={() => setEditing(null)} onOk={submit} confirmLoading={saving} destroyOnClose width={560}>
                <Form form={form} layout="vertical" preserve={false}
                    initialValues={editing ? { name: editing.name || '', description: editing.description || '', i18n: parseI18n(editing.i18n) } : undefined}>
                    <I18nFieldTabs
                        fields={[{ key: 'name', label: t('Name') }, { key: 'description', label: t('Description'), type: 'textarea' }]}
                        base={<>
                            <Form.Item name="name" label={t('Name')} rules={[{ required: true, whitespace: true, max: 100 }]}><Input /></Form.Item>
                            <Form.Item name="description" label={t('Description')} rules={descMax ? [{ max: descMax }] : undefined}><Input.TextArea rows={2} /></Form.Item>
                        </>}
                    />
                </Form>
            </Modal>
        </>
    );
}
